import { apiListCameraTrafficVolumes } from 'apis/CameraTrafficVolume';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, registerables as registerablesJS, Tooltip } from 'chart.js';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { DIALOG_ERROR_GENERAL_ERROR_MESSAGE } from 'constants/message';
import { useDialog } from 'context/DialogContext';
import moment from 'moment';
import 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './CameraTrafficVolumeChart.css';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

// moment.updateLocale('ja', {
//   week: {
//     dow: 1, // First day of week is Monday
//   },
// });

ChartJS.register(...registerablesJS);
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const TIME_FRAME = {
  DAY: 'DATE',
  WEEK: 'WEEK',
  MONTH: 'MONTH'
}

const TRAFFIC_VOLUME_TYPE = {
  VEHICLE: 'VEHICLE',
  HUMAN: 'HUMAN'
}

const CHART_BAR_COLOR = [
  { backgroundColor: 'rgba(51, 102, 204, 0.6)', borderColor: 'rgba(54, 162, 235, 1)' },
  { backgroundColor: 'rgba(255, 102, 51, 0.6)', borderColor: 'rgba(255, 159, 64, 1)' },
  { backgroundColor: 'rgba(201, 203, 207, 0.8)', borderColor: 'rgba(201, 203, 207, 1)' },
  { backgroundColor: 'rgba(255, 205, 86, 0.8)', borderColor: 'rgba(255, 205, 86, 1)' },
  { backgroundColor: 'rgba(75, 192, 192, 0.6)', borderColor: 'rgba(75, 192, 192, 1)' },
  { backgroundColor: 'rgba(102, 153, 51, 0.6)', borderColor: 'rgba(102, 153, 51, 1)' },
]

const CHART_OPTIONS_DEFAULT = {
  responsive: true,
  scales: {
    'y-axis-1': {
      type: 'linear',
      position: 'left',
      title: {
        display: true,
        text: '单位交通量',
      },
    },
    'y-axis-2': {
      type: 'linear',
      position: 'right',
      title: {
        display: true,
        text: '累计交通量',
      },
      grid: {
        drawOnChartArea: false, // Only want the grid lines for one axis
      },
    },
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
    },
  },
}

export default function CameraTrafficVolumeChart({ sensor }) {
  const currentDate = moment().tz('Asia/Tokyo').startOf('day').toDate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    labels: [],
    counts: [],
    accumulatedCounts: []
  })
  const [displayData, setDisplayData] = React.useState({
    labels: [],
    datasets: []
  });
  const [options, setOptions] = React.useState(CHART_OPTIONS_DEFAULT);
  const [scenario, setScenario] = useState(0)
  const [timeUnit, setTimeUnit] = useState(TIME_FRAME.DAY);
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const { error } = useDialog();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    let resizeTimeout;
    let animationTimeout;

    const handleResize = () => {
      // Clear các timeout cũ
      clearTimeout(resizeTimeout);
      clearTimeout(animationTimeout);

      // Tắt animation ngay lập tức
      setOptions(prevOptions => ({
        ...prevOptions,
        animation: { duration: 0 },
      }));

      // Debounce resize
      resizeTimeout = setTimeout(() => {
        setWindowWidth(window.innerWidth);

        // Bật lại animation sau khi resize xong
        animationTimeout = setTimeout(() => {
          setOptions(prevOptions => ({
            ...prevOptions,
            animation: { duration: 1000 },
          }));
        }, 100);
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      clearTimeout(animationTimeout);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { labels, counts, accumulatedCounts } = await downloadData();
        setData({ labels, counts, accumulatedCounts })
        setLoading(false);
      } catch (e) {
        error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE)
        setLoading(false);
      }
    };
    fetchData();
  }, [timeUnit, selectedDate]);

  useEffect(() => {
    const handleDisplayChart = () => {
      const { labels, counts } = data;
      const { datasetLine, datesetBar } = generateChartData(labels, counts);

      if (scenario === 0) {
        setChartDataCombined(labels, datesetBar, datasetLine);
      } else {
        setChartDataSingle(labels, datesetBar, datasetLine);
      }
    }

    handleDisplayChart();
  }, [data, scenario]);

  const listScenario = [1, 2, 3, 4, 5, 6].filter((item) => sensor.data[`trafficVolumeScenario${item}`])

  const getLimitDate = (s = scenario) => {
    const subtractAmount = s === 0 ? Math.max(...listScenario.map((item) => sensor.data[`trafficVolumeStoragePeriod${item}`])) : sensor.data[`trafficVolumeStoragePeriod${s}`];
    return moment().subtract(subtractAmount, 'days').tz('Asia/Tokyo').startOf('day')
  }

  const getLabelByScenario = (scenario) => {
    return `[${sensor.data[`trafficVolumeType${scenario}`] === "VEHICLE" ? "自動車" : "歩行者"}]${sensor.data[`trafficVolumeName${scenario}`].trim()}`
  }
  const scenarioOptions = listScenario.map((item) => ({ value: item, label: getLabelByScenario(item) }))

  const handleChangeTime = (e) => {
    const value = e.target.value;
    setSelectedDate(prev => value ? value : prev);
  }

  const adjustDate = (unit, amount) => {
    setSelectedDate(prevDate =>
      moment(prevDate).tz('Asia/Tokyo').add(amount, unit).toDate()
    );
  };

  const handleNextTime = () => {
    switch (timeUnit) {
      case TIME_FRAME.DAY:
        adjustDate('days', 1);
        break;
      case TIME_FRAME.WEEK:
        adjustDate('weeks', 1);
        break;
      case TIME_FRAME.MONTH:
        adjustDate('months', 1);
        break;
      default:
        break;
    }
  };

  const handlePreviousTime = () => {
    switch (timeUnit) {
      case TIME_FRAME.DAY:
        adjustDate('days', -1);
        break;
      case TIME_FRAME.WEEK:
        adjustDate('weeks', -1);
        break;
      case TIME_FRAME.MONTH:
        adjustDate('months', -1);
        break;
      default:
        break;
    }
  };

  const handleDisplayTime = (date) => {
    const format = timeUnit === TIME_FRAME.MONTH ? 'YYYY-MM' : timeUnit === TIME_FRAME.WEEK ? 'GGGG-[W]WW' : 'YYYY-MM-DD';
    return moment(date).tz('Asia/Tokyo').format(format);
  }

  const handleDownloadCSV = async () => {
    const { labels, counts, accumulatedCounts } = data;
    const csvData = generateCSVData(labels, counts, accumulatedCounts, scenario);
    generateCSV(scenario, csvData);
  }

  const downloadData = async () => {
    let startDate, startTimeQuery, endTimeQuery;
    const limitDate = getLimitDate();
    if (timeUnit === TIME_FRAME.DAY) {
      startDate = moment.max(moment(selectedDate).tz('Asia/Tokyo').startOf('day'), limitDate).toDate();
      startTimeQuery = startDate.toISOString();
      endTimeQuery = moment(selectedDate).tz('Asia/Tokyo').endOf('day').toISOString();
    } else if (timeUnit === TIME_FRAME.WEEK) {
      startDate = moment(selectedDate).tz('Asia/Tokyo').startOf('week').toDate();
      startTimeQuery = moment.max(startDate, limitDate).toISOString();
      endTimeQuery = moment(selectedDate).tz('Asia/Tokyo').endOf('week').toISOString();
    } else if (timeUnit === TIME_FRAME.MONTH) {
      startDate = moment(selectedDate).tz('Asia/Tokyo').startOf('month').toDate();
      startTimeQuery = moment.max(startDate, limitDate).toISOString();
      endTimeQuery = moment(selectedDate).tz('Asia/Tokyo').endOf('month').toISOString();
    }

    const result = await apiListCameraTrafficVolumes(sensor.id, startTimeQuery, endTimeQuery);
    const { counts, accumulatedCounts } = calculateTrafficData(result, startDate);
    const labels = generateLabels(startDate);

    return { counts, accumulatedCounts, labels };
  };

  const calculateTrafficData = (result, startDate) => {
    const unitCount = timeUnit === TIME_FRAME.DAY ? 24 : timeUnit === TIME_FRAME.WEEK ? 7 : moment(startDate).tz('Asia/Tokyo').daysInMonth();
    // Initialize arrays to store traffic data for each hour
    const counts = Array.from({ length: unitCount }, () => Array(6).fill(0));
    const accumulatedCounts = Array.from({ length: unitCount }, () => Array(6).fill(0));

    // Variables to keep track of the running total of accumulated counts for each scenario
    const runningTotalAccumulatedCounts = Array(6).fill(0);

    // Populate counts and accumulatedCounts based on trafficData
    result?.data?.forEach((item) => {
      const date = new Date(item.timestamp);
      let index;
      if (timeUnit === TIME_FRAME.DAY) index = moment(item.timestamp).tz('Asia/Tokyo').hour();
      else if (timeUnit === TIME_FRAME.WEEK) index = Math.floor((date - startDate) / (1000 * 60 * 60 * 24));
      else index = Math.floor((date - startDate) / (1000 * 60 * 60 * 24))

      if (index >= 0 && index < unitCount) {
        const parsedData = JSON.parse(item.data);
        for (let scenarioIndex = 1; scenarioIndex <= 6; scenarioIndex++) {
          const limitDate = getLimitDate(scenarioIndex);
          const countKey = `count_${scenarioIndex}`;
          const countValue = moment(item.timestamp).tz('Asia/Tokyo').isSameOrAfter(limitDate, 'day') ? parsedData[countKey] : 0;
          if (typeof countValue === 'number' && !isNaN(countValue)) {
            counts[index][scenarioIndex - 1] += countValue;
            runningTotalAccumulatedCounts[scenarioIndex - 1] += countValue;
            accumulatedCounts[index][scenarioIndex - 1] = runningTotalAccumulatedCounts[scenarioIndex - 1];
          }
        }
      }
    });

    return { counts, accumulatedCounts };
  };

  const generateLabels = (startDate) => {
    if (timeUnit === TIME_FRAME.DAY) {
      return Array.from({ length: 24 }, (_, i) =>
        moment(startDate).tz('Asia/Tokyo').startOf('day').add(i, 'hours').format('YYYY/MM/DD HH:mm')
      );
    } else {
      const count = timeUnit === TIME_FRAME.WEEK ? 7 : moment(startDate).tz('Asia/Tokyo').daysInMonth();
      return Array.from({ length: count }, (_, i) => {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        return date.toLocaleDateString('ja-JP', { weekday: 'short', month: '2-digit', day: '2-digit', timeZone: 'Asia/Tokyo' });
      });
    }
  };

  const generateChartData = (labels, counts) => {
    let datesetBar, datasetLine;
    if (scenario === 0) {
      // Prepare datasets for each scenario
      const dataset_bar = Array.from({ length: listScenario.length }, (_, i) => counts.map(dayCounts => dayCounts[i]));
      // Calculate cumulative totals for the line chart
      const combinedTotals = dataset_bar[0].map((_, index) => {
        return dataset_bar.reduce((sum, scenario) => sum + scenario[index], 0);
      });

      const dataset_line = combinedTotals.reduce((acc, value, index) => {
        if (index === 0) {
          acc.push(value);
        } else {
          acc.push(acc[index - 1] + value);
        }
        return acc;
      }, []);

      // Combine datasets for the chart
      datesetBar = dataset_bar.map(data => data.slice(0, labels.length));
      datasetLine = dataset_line.slice(0, labels.length);
    } else {
      datesetBar = counts.map(dayCounts => dayCounts[scenario - 1]);
      datasetLine = counts.reduce((acc, dayCounts) => {
        if (acc.length === 0) {
          acc.push(dayCounts[scenario - 1]);
        } else {
          acc.push(acc[acc.length - 1] + dayCounts[scenario - 1]);
        }
        return acc;
      }, [])
    }
    return { datesetBar, datasetLine };
  };

  const setChartDataSingle = (labels, dataset_bar, dataset_line) => {
    const barDatasetLabel = sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.VEHICLE ? '単位交通量' : '単位歩行者数';
    const lineDatasetLabel = sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.VEHICLE ? '累積交通量' : '累積歩行者数';
    const chartLabels = timeUnit === TIME_FRAME.DAY ? labels.map(item => item.slice(11)) : labels;
    const chartData = {
      labels: chartLabels,
      datasets: [
        {
          type: 'bar',
          label: barDatasetLabel,
          data: dataset_bar,
          backgroundColor: 'rgba(54, 162, 235, 0.6)', // Blue color for bars
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
          yAxisID: 'y-axis-1',
          order: 3, // Draw bars first
        },
        {
          type: 'line',
          label: lineDatasetLabel,
          data: dataset_line,
          borderColor: 'rgba(	255, 102, 51, 1)', // Red color for line
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          fill: false,
          yAxisID: 'y-axis-2',
          order: 2, // Draw line on top of bars
        },
      ],
    };
    setOptions({
      responsive: true,
      scales: {
        'y-axis-1': {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: barDatasetLabel,
          },
        },
        'y-axis-2': {
          type: 'linear',
          position: 'right',
          title: {
            display: true,
            text: lineDatasetLabel,
          },
          grid: {
            drawOnChartArea: false, // Only want the grid lines for one axis
          },
        },
      },
    });
    setDisplayData(chartData);
  }

  const setChartDataCombined = (labels, dataset_bar, dataset_line) => {
    const datasets = dataset_bar.map((data, index) => ({
      type: 'bar',
      data: data,
      label: getLabelByScenario(index + 1),
      backgroundColor: CHART_BAR_COLOR[index].backgroundColor,
      borderColor: CHART_BAR_COLOR[index].borderColor,
      borderWidth: 1,
      yAxisID: 'y-axis-1',
      order: 1,
      stack: 'stack1',
    }))
      .concat({
        type: 'line',
        label: '累積交通量',
        data: dataset_line, // Line data for cumulative traffic
        borderColor: 'rgba(0,51,102)', // Black color for line
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        fill: false,
        yAxisID: 'y-axis-2',
        order: 0,
      })
    const chartLabels = timeUnit === TIME_FRAME.DAY ? labels.map(item => item.slice(11)) : labels;

    const chartData = {
      labels: chartLabels,
      datasets
    };
    const options = {
      responsive: true,
      scales: {
        'y-axis-1': {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          title: {
            display: true,
            text: '単位交通量',
          },
          stacked: true, // Enable stacking for bar datasets
        },
        'y-axis-2': {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          title: {
            display: true,
            text: '累積交通量',
          },
          grid: {
            drawOnChartArea: false, // Prevent grid lines from appearing on both Y axes
          },
        }
      },
    };
    setDisplayData(chartData);
    setOptions(options);
  }

  const generateCSVData = (labels, counts, accumulatedCounts, scenario) => {
    return labels.map((label, index) => {
      if (scenario === 0) {
        return {
          date: label,
          count1: counts[index][0],
          count2: counts[index][1],
          count3: counts[index][2],
          count4: counts[index][3],
          count5: counts[index][4],
          count6: counts[index][5],
          unitTraffic: counts[index].reduce((a, b) => a + b, 0),
          cumulativeTraffic: accumulatedCounts[index].reduce((a, b) => a + b, 0),
        };
      } else {
        return {
          date: label,
          unitTraffic: counts[index][scenario - 1],
          cumulativeTraffic: accumulatedCounts[index][scenario - 1],
        };
      }
    });
  };

  function generateCSV(scenario, data) {
    let csvContent = '\uFEFF';
    let headers = ['日付'];
    let filename = ''
    if (scenario === 0) {
      switch (timeUnit) {
        case TIME_FRAME.DAY:
          filename = sensor.name + '_合算_日単位';
          break;
        case TIME_FRAME.WEEK:
          filename = sensor.name + '_合算_週単位';
          break;
        case TIME_FRAME.MONTH:
          filename = sensor.name + '_合算_月単位';
          break;
        default:
          break;
      }
      for (let i = 1; i <= listScenario.length; i++) {
        let name = sensor.data[`trafficVolumeName${i}`] ? getLabelByScenario(i) : `カウント${i}`;
        headers.push(name);
      }
      headers.push('単位交通量', '累積交通量');
      csvContent += headers.join(',') + '\n';

      data.forEach(row => {
        const counts = Array.from({ length: listScenario.length }, (_, i) => row[`count${i + 1}`] || 0).join(',');
        csvContent += `${row.date},${counts},${row.unitTraffic},${row.cumulativeTraffic}\n`;
      });
    } else {
      switch (timeUnit) {
        case TIME_FRAME.DAY:
          filename = sensor.name + '_' + sensor.data[`trafficVolumeName${scenario}`] + '日単位';
          break;
        case TIME_FRAME.WEEK:
          filename = sensor.name + '_' + sensor.data[`trafficVolumeName${scenario}`] + '週単位';
          break;
        case TIME_FRAME.MONTH:
          filename = sensor.name + '_' + sensor.data[`trafficVolumeName${scenario}`] + '月単位';
          break;
        default:
          break;
      }
      csvContent += '日付,単位交通量,累積交通量\n';
      data.forEach(row => {
        csvContent += `${row.date},${row.unitTraffic},${row.cumulativeTraffic}\n`;
      });
    }
    // Code to download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return csvContent;
  }

  return (
    <div className='camera_traffic_volume_chart_container'>
      <div className='camera_traffic_volume_chart_header'>
        <div className='camera_traffic_volume_chart_header_left'>
          <select value={scenario} onChange={(e) => {
            setScenario(parseInt(e.target.value))
          }}>
            <option value={0}>合算</option>
            {scenarioOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <button onClick={handleDownloadCSV} className='button_active'>
            <span>ダウンロード</span>
          </button>
          <div className='camera_traffic_volume_chart_header_left_time_picker'>
            <button onClick={handlePreviousTime}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <input
              type={timeUnit.toLowerCase()}
              value={handleDisplayTime(selectedDate)}
              required
              min={getLimitDate(scenario).format('YYYY-MM-DD')}
              onChange={handleChangeTime}
            />
            <button onClick={handleNextTime}>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
        <div>
          <select value={timeUnit} onChange={(e) => {
            setTimeUnit(e.target.value)
          }}>
            <option value={TIME_FRAME.DAY}>日単位</option>
            <option value={TIME_FRAME.WEEK}>週単位</option>
            <option value={TIME_FRAME.MONTH}>月単位</option>
          </select>
        </div>
      </div>
      {loading && <FanLoadingIcon size={80} />}
      <div className='camera_traffic_volume_chart_bar'><Bar key={windowWidth} options={options} data={displayData} /></div>
    </div>
  )
}

CameraTrafficVolumeChart.propTypes = {
  sensor: PropTypes.object
}